import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../utils/axiosInterceptor';
import { DateTime } from 'luxon';

import {
    Paper, Fab, Typography, Tooltip, Button, Container,
    Dialog, DialogActions, DialogContent, DialogTitle,
    FormControl, TextField, Box, Divider,
    Table, TableContainer, TableCell, TableRow, TableBody, TableHead
} from '@mui/material';

import {
    DataGrid, gridClasses,
    gridPageCountSelector,
    GridPagination,
    useGridApiContext,
    GridToolbar,
    useGridSelector,
} from '@mui/x-data-grid';
import MuiPagination from '@mui/material/Pagination';

// Custom components and functions
import CustomChip from '../../utils/components/CustomChip';
import CustomMessage from '../../utils/components/CustomMessage';
import CustomStatusChip from '../../utils/components/CustomStatusChip';
import useWindowDimensions from '../../utils/functions/useWindowDimensions';
import { formatMoney, getDate, formatMoneyDecimal } from '../../utils/functions/Utils';
import LocaleTextDataGrid from '../../utils/functions/LocaleTextDataGrid';
import { updateProducts, updatePrice } from './cotizacion.controller';
import CotizacionModal from './CotizacionModal';

const TablaCotizaciones = () => {

    let navigate = useNavigate();
    const auth = useSelector((state) => state.auth);

    const [data, setData] = useState([]);
    const [selected, setSelected] = useState('');

    const [open, setOpen] = useState(false);
    const [key, setKey] = useState('');

    const [showDetails, setShowDetails] = useState({});
    const [report, setReport] = useState({});
    const [showReport, setShowReport] = useState(false);
    const [reportFlag, setReportFlag] = useState(false);

    const [apiRes, setApiRes] = useState({ show: false, message: '', severity: '' });
    const { height } = useWindowDimensions();

    useEffect(() => {
        if (!['Admin', 'Vendedor', 'Ayudante', 'Vendedor2'].includes(auth.user.rol)) {
            navigate('/dashboard');
        }
        document.title = 'Tabla de pedidos';
    }, [auth])

    const handleCloseMessage = (show) => {
        setApiRes(state => { return { ...state, show: show } })
    }

    const getData = async () => {
        try {
            let res;
            if (auth.user.rol === 'Admin')
                res = await axios.get('/api/orders');
            else
                res = await axios.get(`/api/orders/seller/${auth.user.name}`);
            if (res.status === 200) {
                setData(res.data);
                document.title = 'Cotizaciones';
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrió un error al cargar las cotizaciones, favor de intentarlo mas tarde', severity: 'error' } });
            console.log(err);
        }
    }

    useEffect(() => {
        getData();
    }, [])

    const renderProducts = () => {
        return (
            <div onKeyDown={handleKeyDown}>
                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'start', alignItems: 'center' }}>
                    <Button size='small' variant='outlined' onClick={() => navigate('/dashboard')}><i className='fad fa-arrow-left'></i> &nbsp;&nbsp;Regresar</Button>
                    <Typography variant='h5' align='left' sx={{ mt: 1, mb: 1, fontWeight: 700, ml: 3 }}>Cotizaciones</Typography>
                </div>
                <Box
                    sx={{
                        [`.${gridClasses.cell}.req`]: {
                            backgroundColor: '#90CAF988',
                            fontWeight: 500,
                            color: 'text.primary',
                        },
                    }}
                >
                    <DataGrid
                        autoPageSize={true}
                        sx={{ '& .MuiDataGrid-toolbarContainer': { position: 'fixed', top: '70px', right: '24px' }, '& .MuiButtonBase-root': { color: 'comp.gray' }, '& .MuiDataGrid-row': { backgroundColor: 'comp.tableBg', color: 'text.primary', height: 'auto' }, '& .MuiDataGrid-columnHeader': { backgroundColor: 'comp.tableHeaderBg', color: 'text.primary' } }}
                        style={{ height: `${height - 120}px`, width: '100%', overflow: 'hidden' }}
                        localeText={LocaleTextDataGrid}
                        rows={data}
                        density='compact'
                        getRowId={(row) => row._id}
                        onCellClick={(e) => { setSelected(e.row._id) }}
                        onCellDoubleClick={(e) => handleShowReport()}
                        columns={[
                            { field: 'noOrden', headerName: '#', flex: 1, minWidth: 60, maxWidth: 70, headerAlign: 'center', align: 'center' },
                            { field: 'cliente', headerName: 'Cliente', align: 'center', headerAlign: 'center', flex: 1, minWidth: 200, maxWidth: 350 },
                            {
                                field: 'estatus',
                                flex: 1,
                                maxWidth: 160,
                                headerName: 'Estatus',
                                headerClassName: 'text-bold',
                                align: 'center',
                                headerAlign: 'center',
                                renderCell: (params) => (
                                    <CustomStatusChip estatus={params.row.estatus} label={params.row.estatus} />
                                ),
                            },
                            { field: 'fechaDeCotizacion', flex: 1, type: 'date', headerName: 'Fecha de Cotización', align: 'center', headerAlign: 'center', maxWidth: 150, valueGetter: (params) => DateTime.fromISO(params.row.fechaDeCotizacion).toJSDate() },
                            { field: 'monto', flex: 1, headerName: 'Monto', align: 'center', headerAlign: 'center', maxWidth: 150, cellClassName: 'text-bold', valueGetter: (params) => params.row.cotizacion?.total || 0, valueFormatter: (params) => `$ ${formatMoney(params.value)}` },
                            {
                                field: 'factura', flex: 1, headerName: 'Facturado', align: 'center', headerAlign: 'center', maxWidth: 80,
                                valueGetter: (params) => params.row.factura?.flag ? '✔' : '✘',
                                cellClassName: (params) => {
                                    if (params.row.factura?.flag && params.row.factura?.requiereFactura)
                                        return 'text-success';
                                    let st1 = params.row.factura.requiereFactura ? 'req' : '';
                                    let st2 = params.row.factura.flag ? 'text-success' : '';
                                    return st1 + st2
                                }
                            },
                            { field: 'requiereFactura', flex: 1, headerName: 'Pendiente Factura', align: 'center', headerAlign: 'center', maxWidth: 200, valueGetter: (params) => params.row.factura?.requiereFactura && !params.row.factura?.flag ? 'Pendiente' : '', },
                            { field: 'operacion', flex: 1, headerName: 'Operación', align: 'center', headerAlign: 'center', maxWidth: 200, },
                            { field: 'descripcion', flex: 1, headerName: 'Descripción', align: 'center', headerAlign: 'center', minWidth: 200, },
                            { field: 'vendedor', flex: 1, headerName: 'Vendedor', align: 'center', headerAlign: 'center', maxWidth: 200, },
                            { field: 'fechaDePago', flex: 1, type: 'date', headerName: 'Fecha de Pago', align: 'center', headerAlign: 'center', maxWidth: 150, valueGetter: (params) => params.row.fechaDePago ? DateTime.fromISO(params.row.fechaDePago).toJSDate() : null },
                            { field: 'fechaDeEntrega', flex: 1, type: 'date', headerName: 'Fecha de Entrega', align: 'center', headerAlign: 'center', maxWidth: 150, valueGetter: (params) => params.row.fechaDeEntrega ? DateTime.fromISO(params.row.fechaDeEntrega).toJSDate() : null },
                            { field: 'observaciones', flex: 1, headerName: 'Observaciones', align: 'center', headerAlign: 'center', minWidth: 160 }

                        ]}
                        pagination
                        slots={{
                            pagination: CustomPagination,
                            toolbar: GridToolbar
                        }}
                        initialState={{
                            ...data.initialState,
                            pagination: { paginationModel: { pageSize: 25 } },
                            columns: {
                                columnVisibilityModel: {
                                    vendedor: false,
                                    operacion: false,
                                    fechaDePago: false,
                                    requiereFactura: false
                                },
                            }
                        }}
                    />
                </Box>
            </div>
        )
    }

    const handleSimulateClick = () => {
        const btn = document.getElementById('open-order');
        btn.click();
    }

    const handleOpen = () => {
        setOpen(true);
    }

    const handleClose = () => {
        setOpen(false);
        setKey('');
        setSelected('');
    }

    const renderDelete = () => {
        return (
            <Dialog
                open={open}
                onClose={handleClose}
            >
                <DialogTitle id="alert-dialog-title">Eliminar cotización</DialogTitle>
                <DialogContent>
                    <Typography variant='body1' align='justify'>
                        ¿Estás seguro que deseas eliminar la cotización seleccionada?
                    </Typography>
                    <Typography variant='body2' align='justify' sx={{ mt: 2 }}>
                        Esta acción no se puede deshacer.
                    </Typography>
                    <FormControl sx={{ mt: 3, minWidth: 100 }} style={{ display: 'flex' }}>
                        <TextField label="Pin" variant="outlined" value={key} onChange={(e) => setKey(e.target.value)} helperText="Ingrese el pin para confirmar" />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancelar
                    </Button>
                    <Button onClick={handleDelete} color="primary" autoFocus>
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>
        )
    }

    const handleDelete = async () => {
        try {
            const res = await axios.post(`/api/orders/delete`, { order: { _id: selected }, key });
            if (res.status === 200) {
                setApiRes(state => { return { ...state, show: true, message: 'Cotización eliminada correctamente', severity: 'success' } });
                setSelected('');
                setKey('');
                handleClose();
                getData();
            }
        } catch (err) {
            setApiRes(state => { return { ...state, show: true, message: 'Pin incorrecto', severity: 'error' } });
        }
    }

    const getReport = async () => {
        try {
            const res = await axios.get(`/api/orders/${selected}`)
            if (res.status === 200) {
                const porcentajeDescuento = res.data.cotizacion.porcentajeDescuento ? res.data.cotizacion.porcentajeDescuento : 0;
                const dolares = res.data.cotizacionUsd ? res.data.cotizacionUsd.flag : false;
                const tipoDeCambio = res.data.cotizacionUsd ? res.data.cotizacionUsd.tipoDeCambio : 1;
                const decimales = res.data.cotizacionUsd ? res.data.cotizacionUsd.decimales : 2;
                const sinIva = res.data.sinIva ? res.data.sinIva : false;

                const prd = updateProducts(res.data.productos, dolares, tipoDeCambio, decimales);
                const co = updatePrice(prd, porcentajeDescuento, decimales, dolares, sinIva);

                const tmpReport = res.data;
                tmpReport.productos = prd;
                tmpReport.cotizacion = co;
                setReport(tmpReport);
            }
        } catch {
            setApiRes(state => { return { ...state, show: true, message: 'Ocurrio un error al solicitar la información del reporte', severity: 'error' } });
        }
    }

    const handleCloseDetails = () => {
        setShowDetails(false);
        setReport({});
    }

    const handleKeyDown = (event) => {
        // Solo ejecuta la lógica si hay filas seleccionadas
        if (selected !== '') {
            switch (event.key) {
                case '<':
                    getReport();
                    setShowDetails(true);
                    setShowReport(false);
                    break;
                default:
                    break;
            }
        }
    };

    const handleShowReport = () => {
        getReport();
        setShowReport(true);
        setShowDetails(false);
    }

    const handelCloseReport = () => {
        setShowReport(false);
        setReport({});
    }

    const renderReport = () => {
        if (report?._id) {
            return (
                <Dialog
                    open={showReport}
                    onClose={handelCloseReport}
                    scroll='paper'
                    maxWidth='xxl'
                >
                    <DialogContent>
                        <CotizacionModal id={selected} />
                    </DialogContent>

                </Dialog>
            )
        }
    }

    const renderDetails = () => {
        if (report?._id) {
            return (
                <Dialog
                    open={showDetails}
                    onClose={handleCloseDetails}
                    fullWidth
                    maxWidth='xl'
                >
                    <DialogContent>
                        <Typography variant='h5' fontWeight={700} align='center'>
                            Cotización {report.noOrden}
                        </Typography>

                        <Box>
                            <Divider className='my-3' textAlign="left">
                                <CustomChip variant='outlined' estatus='AUTORIZADO' label='Datos de la cotización' className='text-bold' />
                            </Divider>
                            <div style={{ maxHeight: '120px', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', marginBottom: '16px', justifyContent: 'center', alignItems: 'center' }}>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Cliente:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.cliente ? report.cliente : ''}</p>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Operación:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.operacion ? report.operacion : ''}</p>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Vendedor:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.vendedor ? report.vendedor : ''}</p>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Fecha:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.fechaDeCotizacion ? getDate(report.fechaDeCotizacion) : ''}</p>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Fecha de pago:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.fechaDePago ? getDate(report.fechaDePago) : ''}</p>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Estatus:</p>
                                    <div style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'><CustomStatusChip label={report.estatus ? report.estatus : ''} estatus={report.estatus ? report.estatus : ''} /></div>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Descripcion:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.descripcion}</p>
                                </div>
                                <div className='d-flex align-items-stretch' style={{ width: '430px' }}>
                                    <p style={{ width: '130px', fontSize: '14px' }} className='mb-1 px-1 text-bold d-flex align-items-center'>Observaciones:</p>
                                    <p style={{ width: '300px', fontSize: '14px' }} className='mb-1 px-1'>{report.observaciones}</p>
                                </div>
                            </div>
                        </Box>
                        <Box>
                            <Divider className='my-3' textAlign="center">
                                <CustomChip variant='outlined' estatus='AUTORIZADO' label='Productos' className='text-bold' />
                            </Divider>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow hover >
                                            <TableCell align='center' sx={{ fontWeight: '700', fontSize: '1em' }}>Clave</TableCell>
                                            <TableCell align='center' sx={{ fontWeight: '700', fontSize: '1em' }}>Cant.</TableCell>
                                            <TableCell align='center' sx={{ fontWeight: '700', fontSize: '1em' }}>Concepto</TableCell>
                                            <TableCell align='right' sx={{ fontWeight: '700', fontSize: '1em' }}>Precio Unitario</TableCell>
                                            <TableCell align='right' sx={{ fontWeight: '700', fontSize: '1em' }}>Importe</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.productos.map((ref, i) => (
                                            <TableRow hover key={ref.key} >
                                                <TableCell size='small' sx={{ fontSize: '1em', fontWeight: '500' }} align='center'>{ref.clave}</TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1em', fontWeight: '500' }} align='center'>{ref.cantidad}</TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1em', fontWeight: '500' }} align='center'>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Box sx={{ width: '50%' }}>
                                                            <Typography sx={{ fontWeight: '500', pb: '4px' }}>{ref.descripcion}</Typography>
                                                            <Typography sx={{ fontSize: '13px', opacity: '50%' }}>costo unitario: {`$ ${formatMoneyDecimal(ref.costoUnitario, 2)}`}</Typography>
                                                            <Typography sx={{ fontSize: '13px', opacity: '50%' }}>categoría: {ref.categoria}</Typography>

                                                        </Box>
                                                        <Box sx={{ width: '50%' }}>
                                                            {ref.imagen !== '' ? <img src={ref.imagen} style={{ height: 'auto', width: 'auto', maxWidth: '260px', margin: '4px', borderRadius: '10px', maxHeight: '90px' }} alt={ref.descripcion} /> : <></>}
                                                        </Box>
                                                    </Box>
                                                </TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1em', minWidth: '135px', fontWeight: '500' }} align='right'>{`$ ${formatMoneyDecimal(ref.precioUnitarioConDescuento, 2)}`}</TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1em', minWidth: '135px', fontWeight: '500' }} align='right'>{`$ ${formatMoneyDecimal(ref.precioUnitarioConDescuento * ref.cantidad, 2)}`}</TableCell>
                                            </TableRow>
                                        ))}
                                        <>
                                            <TableRow hover>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px' }} align='right'><span className='mb-0 text-bold'>Subtotal</span></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px' }} align='right'>{`$ ${formatMoneyDecimal(report.cotizacion.subtotal, 2)}`}</TableCell>
                                            </TableRow>
                                            {
                                                report.cotizacion.porcentajeDescuento > 0 ?
                                                    <TableRow hover>
                                                        <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                        <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                        <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                        <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px' }} align='right'><span className='mb-0 text-bold'>Descuento</span></TableCell>
                                                        <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px', color: 'succes.main' }} align='right'>{`$ ${formatMoneyDecimal(report.cotizacion.descuento, 2)}`}</TableCell>
                                                    </TableRow> : null
                                            }
                                            <TableRow hover>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px' }} align='right'><span className='mb-0 text-bold'>IVA</span></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px' }} align='right'>{`$ ${formatMoneyDecimal(report.cotizacion.iva, 2)}`}</TableCell>
                                            </TableRow>
                                            <TableRow hover>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em' }} align='center'></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px' }} align='right'><span className='mb-0 text-bold'>TOTAL</span></TableCell>
                                                <TableCell size='small' sx={{ fontSize: '1.05em', minWidth: '125px', fontWeight: 700 }} align='right'>{`$ ${formatMoneyDecimal(report.cotizacion.total, 2)}`}</TableCell>
                                            </TableRow>
                                        </>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        {
                            report.formatoCotizacion.pie.length > 0 ?
                                <Box>
                                    <Divider className='my-3' textAlign="left">
                                        <CustomChip variant='outlined' estatus='AUTORIZADO' label='Términos y condiciones' className='text-bold' />
                                    </Divider>
                                    {report.formatoCotizacion.pie.map((item, index) => {
                                        if (item.key !== undefined) {
                                            return (
                                                <Box key={index + 130} style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
                                                    <Typography variant='body2' className='mb-0'><span className='text-bold'>{item.key}</span> &nbsp;{item.value}</Typography>
                                                </Box>
                                            )
                                        } else {
                                            return (
                                                <Box key={index + 760}>
                                                    <Typography variant='body2' className='mb-0'>{item.value}</Typography>
                                                </Box>)
                                        }
                                    })}
                                </Box> : <></>
                        }
                    </DialogContent>

                </Dialog>
            )
        }
    }

    return (
        <Container maxWidth='xxl' sx={{ width: '100%', overflow: 'hidden', margin: '0px', padding: '4px 4px !important' }}>
            <Paper>
                <Link to='/cotizaciones/nueva'>
                    <Tooltip title='Nueva cotización' placement='top' arrow>
                        <Fab aria-label="add" color='success' style={{ position: 'fixed', bottom: '8px', left: '8px', zIndex: '10', color: '#FFF' }}>
                            <i className="fad fa-plus fa-2x"></i>
                        </Fab>
                    </Tooltip>
                </Link>
                <Link target='_blank' to={{ pathname: '/cotizaciones/seguimiento/' }} style={{ textDecoration: 'none' }}>
                    <Tooltip title='Seguimiento' placement='top' arrow>
                        <Fab aria-label="add" color='primary' style={{ position: 'fixed', bottom: '8px', left: '98px', zIndex: '10', color: '#FFF' }}>
                            <i className="fad fa-tasks-alt fa-2x"></i>
                        </Fab>
                    </Tooltip>
                </Link>
                <Link target='_blank' to={{ pathname: '/cotizaciones/cotizador/' }} style={{ textDecoration: 'none' }}>
                    <Tooltip title='Cotizador' placement='top' arrow>
                        <Fab aria-label="add" color='secondary' style={{ position: 'fixed', bottom: '8px', left: '165px', zIndex: '10', color: '#FFF' }}>
                            <i className="fad fa-money-check-edit-alt fa-2x"></i>
                        </Fab>
                    </Tooltip>
                </Link>

                {selected !== '' ?
                    <div>
                        <Link target='_blank' to={{ pathname: '/cotizaciones/editar/' + selected, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                            <Tooltip title='Editar Información' placement='top' arrow>
                                <Fab aria-label="add" color='info' style={{ position: 'fixed', bottom: '8px', left: '232px', zIndex: '10', color: '#FFF' }}>
                                    <i className="fad fa-info-circle fa-2x"></i>
                                </Fab>
                            </Tooltip>
                        </Link>

                        <Link target='_blank' to={{ pathname: '/cotizaciones/cotizacion/' + selected, state: { id: selected } }} style={{ textDecoration: 'none' }}>
                            <Tooltip title='Editar cotización' placement='top' arrow>
                                <Fab id='open-order' aria-label="add" color='warning' style={{ position: 'fixed', bottom: '8px', left: '299px', zIndex: '10', color: '#FFF' }}>
                                    <i className="fad fa-edit fa-2x"></i>
                                </Fab>
                            </Tooltip>
                        </Link>

                        <Tooltip title='Eliminar cotización' placement='top' arrow>
                            <Fab aria-label="add" color='error' style={{ position: 'fixed', bottom: '8px', left: '366px', zIndex: '10', color: '#FFF' }} onClick={handleOpen}>
                                <i className="fad fa-trash fa-2x"></i>
                            </Fab>
                        </Tooltip>

                        <Box sx={{ position: 'fixed', bottom: '10px', left: '463px', zIndex: '10', color: 'palette.text.primary', opacity: '75%', display: { xs: 'none', lg: 'block' } }}>
                            <Typography sx={{ fontSize: '14px' }}> Presiona "+" para ver los detalles de la cotización</Typography>
                        </Box>

                    </div>
                    : null}
                {renderProducts()}
                {renderDelete()}
                {renderDetails()}
                {renderReport()}
                <CustomMessage open={apiRes.show} message={apiRes.message} severity={apiRes.severity} handleCloseMessage={handleCloseMessage} />
            </Paper>
        </Container>
    );
}

function Pagination({ page, onPageChange, className }) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <MuiPagination
            color="primary"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event, newPage - 1);
            }}
        />
    );
}

function CustomPagination(props) {
    return <GridPagination ActionsComponent={Pagination} {...props} />;
}

export default TablaCotizaciones;